'use client';
/**
 * Accès rapides du tableau de bord — demande du 17/08/2026.
 *
 * Une rangée de raccourcis en tête du tableau de bord : cinq FIXES, pensés
 * pour le quotidien des techniciens (lire un document, saisir/consulter le CQI,
 * les maintenances, les calibrations, le planning général), puis les FAVORIS de la personne — un
 * écran, un document ou un équipement qu'elle épingle elle-même via le « + ».
 *
 * Trois décisions structurantes :
 *
 * 1. AUCUNE navigation n'est réinventée. Un raccourci d'écran passe par
 *    `naviguerVers`, exactement comme la barre latérale et la palette ; un
 *    favori-document ouvre la fiche par `openDoc(code)` et un favori-équipement
 *    désigne l'appareil par `selectedEquip` — les deux gestes que la palette de
 *    recherche fait déjà. Un second chemin d'ouverture aurait divergé du
 *    premier à la première évolution.
 *
 * 2. Le sélecteur de favoris ne réindexe rien : il propose les écrans depuis
 *    `FAMILLES_NAV` (libellés + synonymes, filtrés par `droitsNav` — un écran
 *    masqué dans la barre reste introuvable ici), et les documents/équipements
 *    depuis les collections déjà chargées au magasin. Le périmètre de la
 *    recherche est donc celui des droits, sans règle réécrite.
 *
 * 3. Les favoris sont un CONFORT PERSONNEL, pas une donnée du système : ils
 *    vivent en localStorage, sous une clé propre à l'utilisateur connecté
 *    (`session.key`). Pas de champ serveur, pas de route — perdre ses favoris
 *    en changeant de poste est un inconvénient assumé, bien moindre que de
 *    faire transiter des préférences d'affichage par le moteur.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import type { GedView } from '@/lib/types';
import { I, Modale, inputStyle } from '@/components/ui';
import { FAMILLES_NAV, droitsNav, entreeVisible, famillesVisibles, type EntreeNav } from '@/lib/navigation';

/** Repli sur la casse et les accents, comme dans la palette de recherche. */
const norm = (s: unknown) =>
  String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Un favori épinglé. `libelle` est un instantané : si l'objet vit encore au
 *  magasin, le rendu préfère son intitulé du jour. */
export type Favori =
  | { type: 'ecran'; view: GedView; libelle: string }
  | { type: 'doc'; code: string; libelle: string }
  | { type: 'equip'; id: string; code: string; libelle: string };

/** Identité d'un favori — sert au dédoublonnage et au retrait. */
const cleFavori = (f: Favori) =>
  f.type === 'ecran' ? `ecran:${f.view}` : f.type === 'doc' ? `doc:${f.code}` : `equip:${f.id}`;

/** Les raccourcis fixes : les gestes quotidiens des techniciens, et le planning.
 *  Les vues et icônes sont celles de `FAMILLES_NAV` — mêmes noms, mêmes glyphes. */
const FIXES: { view: GedView; icon: string; label: string }[] = [
  { view: 'list', icon: 'doc', label: 'Documents' },
  { view: 'cqi', icon: 'chart', label: 'CQI' },
  { view: 'maintenances', icon: 'wrench', label: 'Maintenances' },
  { view: 'cal', icon: 'scale2', label: 'Calibrations' },
  // Demande du 03/10/2026 : le planning de tout le laboratoire. Filtré comme les
  // autres par `entreeVisible` — absent pour qui n'a pas la section `planning`.
  { view: 'planning', icon: 'history', label: 'Planning général' },
];

/** L'entrée de navigation d'une vue, si le modèle la connaît. */
const entreeDe = (view: GedView): EntreeNav | null => {
  for (const f of FAMILLES_NAV) {
    const e = f.entrees.find((x) => x.view === view);
    if (e) return e;
  }
  return null;
};

/** Clé de rangement des favoris — préfixée par l'utilisateur : deux personnes
 *  qui partagent un poste ne partagent pas leurs épingles. */
const cleStockage = (userKey: string) => `ged.favoris.${userKey || 'anonyme'}`;

function lireFavoris(userKey: string): Favori[] {
  try {
    const brut = window.localStorage.getItem(cleStockage(userKey));
    const liste = brut ? JSON.parse(brut) : [];
    return Array.isArray(liste) ? (liste as Favori[]).filter((f) => f && typeof f === 'object' && 'type' in f) : [];
  } catch {
    return [];
  }
}

function ecrireFavoris(userKey: string, favoris: Favori[]) {
  try {
    window.localStorage.setItem(cleStockage(userKey), JSON.stringify(favoris));
  } catch {
    // Stockage plein ou indisponible : les favoris de la session survivent en
    // mémoire, ils ne seront simplement pas retrouvés au prochain chargement.
  }
}

/** Une tuile de raccourci — fixe ou favori. Le « × » de retrait n'existe que
 *  pour les favoris ; il se révèle au survol ET au focus clavier (une commande
 *  qui n'apparaît qu'à la souris n'existe pas pour qui navigue au clavier). */
function Tuile({ icon, label, detail, onOuvrir, onRetirer }: {
  icon: string; label: string; detail?: string; onOuvrir: () => void; onRetirer?: () => void;
}) {
  const [actif, setActif] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setActif(true)} onMouseLeave={() => setActif(false)}
      onFocus={() => setActif(true)} onBlur={() => setActif(false)}
      style={{ position: 'relative', display: 'inline-flex' }}>
      <button onClick={onOuvrir} title={detail ? `${label} — ${detail}` : label} className="ged-btn"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 9,
          background: '#fff', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
          padding: '9px 14px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
          fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)', boxShadow: 'var(--shadow-sm)',
        }}>
        <I n={icon} s={15} style={{ color: 'var(--color-primary)' }} />
        <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </button>
      {onRetirer && (
        <button onClick={onRetirer} className="ged-btn"
          title={`Retirer « ${label} » des favoris`} aria-label={`Retirer « ${label} » des favoris`}
          style={{
            position: 'absolute', top: -6, insetInlineEnd: -6,
            width: 18, height: 18, lineHeight: 1, padding: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--navy-800, #23324D)', color: '#fff',
            border: 'none', borderRadius: '50%', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 700,
            opacity: actif ? 1 : 0, pointerEvents: actif ? 'auto' : 'none',
            transition: 'opacity 0.12s',
          }}>
          ×
        </button>
      )}
    </div>
  );
}

export function AccesRapides() {
  const { docs, equips, session, rhAcces, naviguerVers, openDoc } = useGed();
  const droits = React.useMemo(() => droitsNav(session, !!rhAcces?.acces), [session, rhAcces]);

  // Les fixes ne s'affichent que si le profil a accès à l'écran visé : le
  // filtre est `entreeVisible`, le même que la barre latérale et la palette.
  const fixes = FIXES.filter((r) => {
    const e = entreeDe(r.view);
    return e ? entreeVisible(e, droits) : false;
  });

  const [favoris, setFavoris] = React.useState<Favori[]>([]);
  const [selecteur, setSelecteur] = React.useState(false);

  // Chargés à l'arrivée de la session (et rechargés si l'utilisateur change) :
  // la clé de stockage porte `session.key`, qui n'est connue qu'authentifié.
  /* Ajusté au rendu : la lecture est SYNCHRONE (stockage local), et l'effet
     faisait rendre une première fois sans favoris puis une seconde avec — les
     raccourcis apparaissaient après coup, à chaque arrivée. */
  const [sessionPrec, setSessionPrec] = React.useState(session.key);
  if (session.key !== sessionPrec) {
    setSessionPrec(session.key);
    if (session.authenticated) setFavoris(lireFavoris(session.key));
  }

  const ajouter = (f: Favori) => {
    setFavoris((prec) => {
      if (prec.some((x) => cleFavori(x) === cleFavori(f))) return prec;
      const suiv = [...prec, f];
      ecrireFavoris(session.key, suiv);
      return suiv;
    });
    setSelecteur(false);
  };
  const retirer = (f: Favori) => {
    setFavoris((prec) => {
      const suiv = prec.filter((x) => cleFavori(x) !== cleFavori(f));
      ecrireFavoris(session.key, suiv);
      return suiv;
    });
  };

  /** Rendu d'un favori : icône selon sa nature, intitulé vivant si l'objet est
   *  encore au magasin, instantané sinon. Un favori-écran dont l'écran n'est
   *  plus visible pour ce profil est passé sous silence — l'afficher serait
   *  offrir un bouton vers un écran refusé. */
  type FavoriRendu = { f: Favori; icon: string; label: string; detail?: string; ouvrir: () => void };
  const rendus = favoris.flatMap((f): FavoriRendu[] => {
    if (f.type === 'ecran') {
      const e = entreeDe(f.view);
      if (!e || !entreeVisible(e, droits)) return [];
      return [{
        f, icon: e.icon, label: e.label,
        ouvrir: () => naviguerVers(f.view),
      }];
    }
    if (f.type === 'doc') {
      const d = docs.find((x) => x.code === f.code);
      return [{
        f, icon: 'doc', label: f.code, detail: d?.titre || f.libelle,
        ouvrir: () => openDoc(f.code),
      }];
    }
    const eq = equips.find((x) => x.documentId === f.id);
    return [{
      f, icon: 'automate', label: eq?.nom || f.libelle, detail: eq?.code || f.code,
      ouvrir: () => naviguerVers('equip', { cle: 'selectedEquip', valeur: f.id }),
    }];
  });

  return (
    <section aria-label="Accès rapides" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {fixes.map((r) => (
        <Tuile key={r.view} icon={r.icon} label={r.label} onOuvrir={() => naviguerVers(r.view)} />
      ))}
      {rendus.map((x) => (
        <Tuile key={cleFavori(x.f)} icon={x.icon} label={x.label} detail={x.detail}
          onOuvrir={x.ouvrir} onRetirer={() => retirer(x.f)} />
      ))}
      <button onClick={() => setSelecteur(true)} className="ged-btn"
        title="Ajouter un raccourci — un écran, un document ou un équipement"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          background: 'transparent', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-md)',
          padding: '9px 13px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
          fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)',
        }}>
        <I n="plus" s={13} />Ajouter
      </button>
      {selecteur && (
        <SelecteurFavori
          droits={droits}
          dejaLa={new Set([...FIXES.map((r) => `ecran:${r.view}`), ...favoris.map(cleFavori)])}
          onChoisir={ajouter}
          onFerme={() => setSelecteur(false)} />
      )}
    </section>
  );
}

/** Une proposition du sélecteur, quelle que soit sa nature. */
type Proposition = {
  id: string;
  groupe: 'Écrans' | 'Documents' | 'Équipements';
  icon: string;
  titre: string;
  detail: string;
  /** Texte réellement fouillé (déjà normalisé) — synonymes compris pour les écrans. */
  index: string;
  favori: Favori;
};

/** Le sélecteur de favori : un champ de recherche, trois familles de résultats.
 *  Même logique de filtrage que la palette (tous les termes présents, accents
 *  ignorés), mêmes sources — rien n'est indexé de neuf. */
function SelecteurFavori({ droits, dejaLa, onChoisir, onFerme }: {
  droits: ReturnType<typeof droitsNav>;
  dejaLa: Set<string>;
  onChoisir: (f: Favori) => void;
  onFerme: () => void;
}) {
  const { docs, equips } = useGed();
  const [q, setQ] = React.useState('');
  const champRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    const t = window.setTimeout(() => champRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  const propositions: Proposition[] = React.useMemo(() => {
    const r: Proposition[] = [];
    // Les écrans : périmètre exact de la barre latérale — `famillesVisibles`
    // applique les mêmes conditions de profil. Le tableau de bord est exclu :
    // un raccourci vers l'écran qu'on a sous les yeux ne mène nulle part.
    for (const fam of famillesVisibles(droits)) {
      for (const e of fam.entrees) {
        r.push({
          id: `ecran:${e.view}`, groupe: 'Écrans', icon: e.icon, titre: e.label, detail: fam.titre,
          index: norm(`${e.label} ${e.synonymes || ''} ${fam.titre}`),
          favori: { type: 'ecran', view: e.view, libelle: e.label },
        });
      }
    }
    for (const d of docs) {
      r.push({
        id: `doc:${d.code}`, groupe: 'Documents', icon: 'doc', titre: d.code, detail: d.titre,
        index: norm(`${d.code} ${d.titre}`),
        favori: { type: 'doc', code: d.code, libelle: d.titre },
      });
    }
    for (const e of equips) {
      r.push({
        id: `equip:${e.documentId}`, groupe: 'Équipements', icon: 'automate', titre: e.nom,
        detail: [e.code, e.localisation].filter(Boolean).join(' · '),
        index: norm(`${e.code || ''} ${e.nom} ${e.marque || ''} ${e.localisation || ''}`),
        favori: { type: 'equip', id: e.documentId, code: e.code || '', libelle: e.nom },
      });
    }
    return r.filter((p) => !dejaLa.has(p.id));
  }, [droits, docs, equips, dejaLa]);

  const termes = norm(q).split(/\s+/).filter(Boolean);
  const trouves = termes.length
    ? propositions.filter((p) => termes.every((t) => p.index.includes(t)))
    : [];
  const groupes = (['Écrans', 'Documents', 'Équipements'] as const)
    .map((groupe) => ({ groupe, items: trouves.filter((p) => p.groupe === groupe).slice(0, 6) }))
    .filter((g) => g.items.length);

  return (
    <Modale titre="Ajouter un raccourci"
      sousTitre="Un écran, un document ou un équipement — il s’épinglera au tableau de bord."
      onClose={onFerme} largeur={560}>
      <div style={{ display: 'grid', gap: 8 }}>
        <input
          ref={champRef} value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher… (CQI, BIO24-PR-001, Cobas)"
          aria-label="Rechercher un élément à épingler"
          style={{ ...inputStyle, fontSize: 13.5 }} />
        <div style={{ maxHeight: '46vh', overflowY: 'auto' }}>
          {!termes.length && (
            <p style={{ margin: 0, padding: '14px 2px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Tapez le nom d’un <strong>écran</strong> (« CQI », « métrologie »), le <strong>code d’un
              document</strong> ou le nom d’un <strong>équipement</strong> : le raccourci s’ajoutera
              à la suite des accès rapides.
            </p>
          )}
          {!!termes.length && !groupes.length && (
            <p style={{ margin: 0, padding: '14px 2px', fontSize: 13, color: 'var(--text-muted)' }}>
              Rien ne correspond à « {q.trim()} » — ou c’est déjà épinglé.
            </p>
          )}
          {groupes.map((g) => (
            <div key={g.groupe}>
              <div style={{ padding: '8px 2px 4px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                {g.groupe}
              </div>
              {g.items.map((p) => (
                <button key={p.id} onClick={() => onChoisir(p.favori)} className="ged-btn"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'start',
                    background: 'transparent', border: 'none', borderRadius: 'var(--radius-sm)',
                    padding: '7px 8px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
                  }}>
                  <I n={p.icon} s={14} style={{ color: 'var(--color-primary)', flex: 'none' }} />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', flex: 'none' }}>{p.titre}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.detail}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Modale>
  );
}
